/**
 * BroadcastEmailCard — admin tool to compose, schedule and send custom
 * emails to any segment of practices and/or lead-magnet leads.
 *
 * Lives under the System tab in /admin. Click "+ New Broadcast" to open
 * the composer modal.
 */
import React, { useEffect, useState } from 'react';
import {
  Megaphone, Mail, Loader2, Plus, RefreshCw, Trash2,
  Send, CalendarClock, FileText, AlertTriangle, BarChart3,
} from 'lucide-react';
import BroadcastComposer from './BroadcastComposer';
import BroadcastAnalytics from './BroadcastAnalytics';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft:     { bg: 'bg-slate-100',  fg: 'text-slate-700',   label: 'Draft' },
  scheduled: { bg: 'bg-amber-100',  fg: 'text-amber-800',   label: 'Scheduled' },
  sending:   { bg: 'bg-blue-100',   fg: 'text-blue-800',    label: 'Sending…' },
  sent:      { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'Sent' },
  failed:    { bg: 'bg-rose-100',   fg: 'text-rose-800',    label: 'Failed' },
  cancelled: { bg: 'bg-slate-100',  fg: 'text-slate-500',   label: 'Cancelled' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (e) { return iso; }
};

export default function BroadcastEmailCard({ adminToken }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [composerId, setComposerId] = useState(null); // null = closed, 'new' = new, or broadcast id
  const [analyticsId, setAnalyticsId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/broadcasts`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`Load failed: ${res.status}`);
      const data = await res.json();
      setItems(data.broadcasts || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this broadcast? This cannot be undone.')) return;
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/broadcasts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const onCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled broadcast? It will revert to a draft.')) return;
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/broadcasts/${id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`Cancel failed: ${res.status}`);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid="admin-broadcast-card"
      className="bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-200 rounded-xl p-5 space-y-4"
    >
      <div className="flex items-start gap-3">
        <div className="bg-indigo-600 text-white rounded-lg p-2.5">
          <Megaphone className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-indigo-900">Broadcast Emails</h3>
          <p className="text-xs text-indigo-800 mt-0.5 max-w-3xl leading-snug">
            Compose any email and send to any subset of your subscribers.
            Filter by status or source, use merge tokens like
            <code className="mx-1 px-1 bg-white/80 rounded text-[11px] font-mono">{'{{first_name}}'}</code>
            and
            <code className="mx-1 px-1 bg-white/80 rounded text-[11px] font-mono">{'{{practice_name}}'}</code>,
            test to your inbox, then send immediately or schedule for a later date.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setComposerId('new')}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-bold"
          data-testid="broadcast-new-btn"
        >
          <Plus className="w-4 h-4" /> New Broadcast
        </button>
        <button
          onClick={load}
          disabled={loading}
          className="bg-white hover:bg-indigo-50 border-2 border-indigo-300 text-indigo-800 px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-bold disabled:opacity-50"
          data-testid="broadcast-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-300 text-rose-900 text-sm rounded-lg p-3 flex items-start gap-2" data-testid="broadcast-error">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-indigo-200 rounded-lg p-8 text-center" data-testid="broadcast-empty">
          <Mail className="w-10 h-10 text-indigo-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-indigo-900">No broadcasts yet</p>
          <p className="text-xs text-indigo-700 mt-1">Click "New Broadcast" to compose your first email.</p>
        </div>
      ) : (
        <div className="bg-white border border-indigo-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-indigo-50 text-indigo-900">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Name / Subject</th>
                <th className="text-left px-3 py-2 font-bold w-28">Status</th>
                <th className="text-left px-3 py-2 font-bold w-44">When</th>
                <th className="text-left px-3 py-2 font-bold w-24">Stats</th>
                <th className="text-right px-3 py-2 font-bold w-44">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const s = STATUS_STYLES[b.status] || STATUS_STYLES.draft;
                const isBusy = busyId === b.id;
                const canEdit = ['draft', 'scheduled', 'failed'].includes(b.status);
                const when = b.scheduledAt ? `Scheduled: ${fmtDate(b.scheduledAt)}` :
                             b.sentAt    ? `Sent: ${fmtDate(b.sentAt)}` :
                                           `Updated: ${fmtDate(b.updatedAt)}`;
                return (
                  <tr key={b.id} className="border-t border-slate-100 hover:bg-indigo-50/40" data-testid={`broadcast-row-${b.id}`}>
                    <td className="px-3 py-2">
                      <p className="font-bold text-slate-900 leading-tight">{b.name || 'Untitled'}</p>
                      <p className="text-xs text-slate-500 truncate max-w-[300px]">{b.subject || '(no subject)'}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${s.bg} ${s.fg}`}>
                        {s.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700">{when}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {b.stats?.sent ?? 0}/{b.stats?.recipients ?? 0}
                      {b.stats?.failed > 0 && (
                        <span className="ml-1 text-rose-600 font-bold">({b.stats.failed} failed)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {(b.status === 'sent' || b.status === 'sending') && (
                        <button
                          onClick={() => setAnalyticsId(b.id)}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 font-bold px-2 py-1 rounded hover:bg-emerald-50"
                          data-testid={`broadcast-analytics-${b.id}`}
                        >
                          <BarChart3 className="w-3.5 h-3.5" /> Analytics
                        </button>
                      )}
                      <button
                        onClick={() => setComposerId(b.id)}
                        className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 font-bold px-2 py-1 rounded hover:bg-indigo-50"
                        data-testid={`broadcast-open-${b.id}`}
                      >
                        <FileText className="w-3.5 h-3.5" /> {canEdit ? 'Edit' : 'View'}
                      </button>
                      {b.status === 'scheduled' && (
                        <button
                          onClick={() => onCancel(b.id)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-bold px-2 py-1 rounded hover:bg-amber-50 disabled:opacity-50"
                          data-testid={`broadcast-cancel-${b.id}`}
                        >
                          <CalendarClock className="w-3.5 h-3.5" /> Cancel
                        </button>
                      )}
                      {b.status !== 'sent' && b.status !== 'sending' && (
                        <button
                          onClick={() => onDelete(b.id)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900 font-bold px-2 py-1 rounded hover:bg-rose-50 disabled:opacity-50"
                          data-testid={`broadcast-delete-${b.id}`}
                        >
                          {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {composerId && (
        <BroadcastComposer
          adminToken={adminToken}
          broadcastId={composerId === 'new' ? null : composerId}
          onClose={() => { setComposerId(null); load(); }}
        />
      )}
      {analyticsId && (
        <BroadcastAnalytics
          adminToken={adminToken}
          broadcastId={analyticsId}
          onClose={() => setAnalyticsId(null)}
        />
      )}
    </div>
  );
}
